import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  ButtonGroup,
  Grid,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Add,
  Warning,
  AutoMode,
  FilterList,
  ViewWeek,
  ViewDay,
  CalendarMonth,
  ViewModule
} from '@mui/icons-material';
import { DndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import { API_URL } from '../utils/apiConfig';
import { AuthContext } from '../context/AuthContext';
import ShiftEditDialog from './ShiftEditDialog';
import AutoScheduleDialog from './AutoScheduleDialog';
import EmployeePickerDialog from './EmployeePickerDialog';
// import ShiftTooltip from './ShiftTooltip';
import HoursSummary from './HoursSummary';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InlineShiftEditor from './InlineShiftEditor';
import ShiftActionPopover from './ShiftActionPopover';
import QuickCreatePopover from './QuickCreatePopover';

// Color schemes
const ROLE_COLORS = {
  'Manager': '#9c27b0',
  'Assistant Manager': '#673ab7',
  'Team Lead': '#3f51b5',
  'Shift Lead': '#5c6bc0',
  'Cashier': '#2196f3',
  'Sales Associate': '#4caf50',
  'Front Desk': '#ff9800',
  'Inventory': '#607d8b',
  'Barista': '#795548',
  'Server': '#00897b',
  'Kitchen': '#f57c00',
  'Bartender / Server': '#00acc1',
  'Test Role': '#757575'
};

// Get contrast text color
const getContrastText = (bgColor) => {
  const r = parseInt(bgColor.slice(1, 3), 16);
  const g = parseInt(bgColor.slice(3, 5), 16);
  const b = parseInt(bgColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000' : '#fff';
};

// Local date helpers to avoid UTC off-by-one
const pad2 = (n) => String(n).padStart(2, '0');
const formatLocalDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Shift block component
function ShiftBlock({ shift, employee, onClick, isCompact = false, onEdit, onDelete, onCopy, onReassign, onContextMenu, onDoubleClick }) {
  const parseLocal = (isoStr) => new Date(String(isoStr).replace(' ', 'T'));
  const formatTime = (date) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

  const start = parseLocal(shift.start_ts);
  const end = parseLocal(shift.end_ts);
  const duration = Math.round((end - start) / (1000 * 60 * 60) * 10) / 10;

  const bgColor = ROLE_COLORS[shift.role] || '#757575';
  const textColor = getContrastText(bgColor);

  if (isCompact) {
    return (
      <Box
        role="button"
        onClick={(e) => onClick(e, shift)}
        onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick && onDoubleClick(e, shift); }}
        onContextMenu={(e) => { e.preventDefault(); onContextMenu && onContextMenu(e, shift); }}
        sx={{
          bgcolor: bgColor,
          color: textColor,
          borderRadius: 0.5,
          px: 0.75,
          py: 0.25,
          mb: 0.25,
          width: '100%',
          cursor: 'pointer',
          fontSize: 11,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          '&:hover': { opacity: 0.9 }
        }}
      >
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <strong>{shift.role}</strong> {formatTime(start)}-{formatTime(end)}
        </Box>
        {!shift.employee_id && (
          <Warning sx={{ fontSize: 14, opacity: 0.9 }} />
        )}
      </Box>
    );
  }

  const shiftContent = (
    <Box
      onClick={(e) => onClick(e, shift)}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick && onDoubleClick(e, shift); }}
      onContextMenu={(e) => { e.preventDefault(); onContextMenu && onContextMenu(e, shift); }}
      sx={{
        backgroundColor: bgColor,
        color: textColor,
        borderRadius: 1,
        p: 0.5,
        mb: 0.5,
        cursor: 'pointer',
        fontSize: 11,
        lineHeight: 1.2,
        minHeight: 24,
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': {
          opacity: 0.9,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transform: 'translateY(-1px)'
        }
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
        {shift.role}
      </Typography>
      <Typography sx={{ fontSize: 10 }}>
        {formatTime(start)} - {formatTime(end)}
      </Typography>
      {employee && (
        <Typography sx={{ fontSize: 9, opacity: 0.9 }}>
          {employee.first_name} {employee.last_name}
        </Typography>
      )}
      {!shift.employee_id && (
        <Warning sx={{ fontSize: 12, position: 'absolute', top: 2, right: 2 }} />
      )}
    </Box>
  );

  return shiftContent;
}

// Draggable wrapper for shifts
function DraggableShift({ shift, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `shift-${shift.id}`, data: { type: 'shift', shift } });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return (
    <div ref={setNodeRef} style={{ ...style, opacity: isDragging ? 0.6 : 1 }} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

// Droppable day cell (roster view)
function DayCell({ employee, day, children, onEmptySpaceClick }) {
  const dateStr = formatLocalDate(day);
  const { setNodeRef, isOver } = useDroppable({ id: `cell-${employee.id}-${dateStr}`, data: { type: 'cell', employeeId: employee.id, date: dateStr } });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        p: 1,
        borderBottom: '1px solid #e5e7eb',
        borderRight: '1px solid #e5e7eb',
        minHeight: 70,
        backgroundColor: isOver ? '#ecfeff' : '#ffffff',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background-color 0.2s ease'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget || e.target.closest('.shift-block') === null) {
          const bounds = e.currentTarget.getBoundingClientRect();
          const relY = (e.clientY - bounds.top) / Math.max(1, bounds.height);
          const defaultStart = relY < 0.5 ? '09:00' : '13:00';
          const defaultEnd = relY < 0.5 ? '13:00' : '17:00';
          onEmptySpaceClick && onEmptySpaceClick(day, employee, { startTime: defaultStart, endTime: defaultEnd }, e);
        }
      }}
    >
      {children}
    </Box>
  );
}

// Employee row component for roster view
function EmployeeRow({ employee, shifts, days, onShiftClick, onEmptySpaceClick, showHours = false, onShiftEdit, onShiftDelete, onShiftCopy, onShiftReassign, onShiftContextMenu, onShiftDoubleClick }) {
  const totalHours = shifts.reduce((sum, shift) => {
    const start = new Date(String(shift.start_ts).replace(' ', 'T'));
    const end = new Date(String(shift.end_ts).replace(' ', 'T'));
    return sum + (end - start) / (1000 * 60 * 60);
  }, 0);

  return (
    <Box sx={{ display: 'contents' }}>
      {/* Employee info column */}
      <Box sx={{
        p: 2,
        borderBottom: '1px solid #e5e7eb',
        borderRight: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        minHeight: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1
      }}>
        <Box>
          <Typography variant="body2" sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#111827',
            lineHeight: 1.2,
            whiteSpace: 'nowrap'
          }}>
            {employee.first_name} {employee.last_name}
          </Typography>
          <Typography variant="caption" sx={{
            color: '#6b7280',
            fontSize: '0.7rem',
            lineHeight: 1.2,
            display: 'block'
          }}>
            {employee.role}
          </Typography>
        </Box>
        {showHours && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{
              fontWeight: 600,
              color: totalHours > 40 ? '#dc2626' : '#059669',
              fontSize: '0.875rem'
            }}>
              {totalHours.toFixed(1)}h
            </Typography>
          </Box>
        )}
      </Box>

      {/* Day columns */}
      {days.map((day, dayIndex) => {
        const dayShifts = shifts.filter(shift => {
          const shiftStart = new Date(String(shift.start_ts).replace(' ', 'T'));
          return shiftStart.toDateString() === day.toDateString();
        });

        const dayHours = dayShifts.reduce((sum, s) => {
          const s1 = new Date(String(s.start_ts).replace(' ', 'T'));
          const e1 = new Date(String(s.end_ts).replace(' ', 'T'));
          return sum + (e1 - s1) / (1000 * 60 * 60);
        }, 0);

        return (
          <DayCell key={dayIndex} employee={employee} day={day} onEmptySpaceClick={onEmptySpaceClick}>
            {dayShifts.map(shift => (
              <Box key={shift.id} className="shift-block">
                <DraggableShift shift={shift}>
                  <ShiftBlock
                    shift={shift}
                    employee={employee}
                    onClick={(e) => onShiftClick(e, shift)}
                    isCompact={true}
                    onEdit={onShiftEdit}
                    onDelete={onShiftDelete}
                    onCopy={onShiftCopy}
                    onReassign={onShiftReassign}
                    onContextMenu={onShiftContextMenu}
                    onDoubleClick={onShiftDoubleClick}
                  />
                </DraggableShift>
              </Box>
            ))}
            {dayShifts.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary',
                  fontSize: 12,
                  opacity: 0.7,
                  '&:hover': {
                    opacity: 1
                  }
                }}
              >
                Click to add shift
              </Box>
            )}

            {/* Per-day hours for this employee */}
            <Box sx={{ position: 'absolute', right: 6, bottom: 4, pointerEvents: 'none' }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: dayHours > 8 ? '#dc2626' : '#6b7280',
                  backgroundColor: dayHours > 8 ? 'rgba(252, 165, 165, 0.25)' : 'transparent',
                  px: 0.5,
                  borderRadius: 0.5
                }}
              >
                {dayHours > 0 ? `${dayHours.toFixed(1)}h` : ''}
              </Typography>
            </Box>
            </DayCell>
        );
      })}
    </Box>
  );
}

export default function SchedulingCalendar() {
  const { user } = useContext(AuthContext) || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', '2week', 'month'
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [empQuery, setEmpQuery] = useState('');
  const [empRole, setEmpRole] = useState('');
  const [shiftEditDialog, setShiftEditDialog] = useState({ open: false, shift: null, selectedDate: null });
  const [autoScheduleDialog, setAutoScheduleDialog] = useState(false);
  const [employeePickerDialog, setEmployeePickerDialog] = useState({ open: false, date: null, employee: null });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [coverageTotals, setCoverageTotals] = useState({ daily: [], byEmployee: [] });
  const SCHED_FEATURES = process.env.REACT_APP_SCHED_FEATURES === 'true';
  const [coverageDialog, setCoverageDialog] = useState({ open: false, date: null, results: [] });
  const [coverageSummary, setCoverageSummary] = useState([]);
  const [contextMenu, setContextMenu] = useState({ open: false, mouseX: null, mouseY: null, shift: null });
  const [inlineEditor, setInlineEditor] = useState({ open: false, anchorEl: null, shift: null });
  const [actionPopover, setActionPopover] = useState({ open: false, anchorEl: null, shift: null, employee: null });
  const [quickCreate, setQuickCreate] = useState({ open: false, anchorEl: null, date: null, employee: null, startTime: '09:00', endTime: '13:00' });

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!active || !over) return;
    const shift = active.data?.current?.shift;
    const overData = over.data?.current;
    if (!shift || !overData || overData.type !== 'cell') return;

    const targetEmployeeId = overData.employeeId;
    const targetDate = overData.date; // YYYY-MM-DD (local)

    let startTime = shift.start_ts.split(' ')[1] || '09:00:00';
    let endTime = shift.end_ts.split(' ')[1] || '17:00:00';
    if (startTime.length === 5) startTime = `${startTime}:00`;
    if (endTime.length === 5) endTime = `${endTime}:00`;

    const newStart = `${targetDate} ${startTime}`;
    const newEnd = `${targetDate} ${endTime}`;

    // If nothing actually changed, skip
    const oldDate = String(shift.start_ts).slice(0, 10);
    const sameDay = oldDate === targetDate;
    const sameEmp = Number(shift.employee_id) === Number(targetEmployeeId);
    if (sameDay && sameEmp) return;

    try {
      await handleSaveShift({
        schedule_id: selectedSchedule?.id,
        role: shift.role,
        start_ts: newStart,
        end_ts: newEnd,
        break_minutes: shift.break_minutes || 0,
        employee_id: targetEmployeeId,
        notes: shift.notes || null
      }, shift.id);
    } catch (e) {
      console.error('DnD update failed:', e);
      alert(e.message);
    }
  };

  // Generate date range based on view mode
  const dateRange = useMemo(() => {
    const start = new Date(selectedDate);
    let days = [];

    switch (viewMode) {
      case 'day':
        days = [new Date(start)];
        break;
      case 'week':
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday start
        const monday = new Date(start.setDate(diff));
        for (let i = 0; i < 7; i++) {
          const day = new Date(monday);
          day.setDate(monday.getDate() + i);
          days.push(day);
        }
        break;
      case '2week':
        const dayOfWeek2 = start.getDay();
        const diff2 = start.getDate() - dayOfWeek2 + (dayOfWeek2 === 0 ? -6 : 1);
        const monday2 = new Date(start.setDate(diff2));
        for (let i = 0; i < 14; i++) {
          const day = new Date(monday2);
          day.setDate(monday2.getDate() + i);
          days.push(day);
        }
        break;
      case 'month':
        const year = start.getFullYear();
        const month = start.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay() + 1); // Monday start

        for (let d = new Date(startDate); d <= lastDay || days.length < 42; d.setDate(d.getDate() + 1)) {
          days.push(new Date(d));
          if (days.length >= 42) break; // 6 weeks max
        }
        break;
    }
    return days;
  }, [selectedDate, viewMode]);

  const weekStartStr = useMemo(() => (
    dateRange[0] ? formatLocalDate(dateRange[0]) : formatLocalDate(selectedDate)
  ), [dateRange, selectedDate]);

  const weekEndStr = useMemo(() => (
    dateRange.length > 0 ? formatLocalDate(dateRange[dateRange.length - 1]) : formatLocalDate(selectedDate)
  ), [dateRange, selectedDate]);

  // Utility functions
  const parseLocal = (isoStr) => new Date(String(isoStr).replace(' ', 'T'));
  const formatTime = (date) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

  // Navigation functions
  const navigate = useCallback((direction) => {
    const newDate = new Date(selectedDate);
    switch (viewMode) {
      case 'day':
        newDate.setDate(newDate.getDate() + direction);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (direction * 7));
        break;
      case '2week':
        newDate.setDate(newDate.getDate() + (direction * 14));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + direction);
        break;
    }
    setSelectedDate(newDate);
  }, [selectedDate, viewMode]);

  const goToToday = useCallback(() => {
    setSelectedDate(new Date());
  }, []);

  // Format header title based on view
  const getHeaderTitle = () => {
    const options = { month: 'long', year: 'numeric' };
    switch (viewMode) {
      case 'day':
        return selectedDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
      case 'week':
      case '2week':
        const start = dateRange[0];
        const end = dateRange[dateRange.length - 1];
        if (start.getMonth() === end.getMonth()) {
          return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
        }
        return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${start.getFullYear()}`;
      case 'month':
        return selectedDate.toLocaleDateString('en-US', options);
      default:
        return '';
    }
  };

  // Fetch locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/locations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json();
        if (resp.ok) {
          setLocations(data.data || []);
        }
      } catch (e) {
        console.error('Error fetching locations:', e);
      }
    };
    fetchLocations();
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');

        // Fetch schedules
        const schedParams = new URLSearchParams();
        // When 'all' is selected, don't filter by location to get all schedules
        if (selectedLocation !== 'all') {
          schedParams.set('locationId', selectedLocation);
        }
        schedParams.set('start', weekStartStr);
        schedParams.set('end', weekEndStr);

        const schedResp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/schedules?${schedParams.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const schedData = await schedResp.json();
        if (schedResp.ok) {
          const schedules = schedData.data || [];
          setSchedules(schedules);

          // When filtering by location, prefer schedule matching that location
          let match = schedules.find(s =>
            String(s.week_start).slice(0, 10) === weekStartStr &&
            (selectedLocation === 'all' || s.location_id == selectedLocation)
          );

          // Fallback to any schedule matching week
          if (!match) {
            match = schedules.find(s => String(s.week_start).slice(0, 10) === weekStartStr);
          }

          if (match) setSelectedSchedule(match);
          else if (schedules.length > 0) setSelectedSchedule(schedules[0]);
        }

        // Fetch employees
        const empParams = new URLSearchParams();
        if (selectedLocation !== 'all') {
          empParams.set('locationId', selectedLocation);
        }
        if (empQuery) empParams.set('q', empQuery);
        if (empRole) empParams.set('role', empRole);
        empParams.set('limit', '100');

        const empResp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/employees?${empParams.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const empData = await empResp.json();
        if (empResp.ok) {
          setEmployees(empData.data || []);
        }

      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, weekStartStr, weekEndStr, empQuery, empRole, selectedLocation]);

  // Fetch shifts when schedule changes
  useEffect(() => {
    const fetchShifts = async () => {
      try {
        const token = localStorage.getItem('token');

        if (selectedLocation === 'all' && schedules.length > 0) {
          // Multi-location mode: fetch shifts for all schedules
          const responses = await Promise.all(
            schedules.map(schedule =>
              fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts?scheduleId=${schedule.id}`, {
                headers: { Authorization: `Bearer ${token}` }
              }).then(r => r.json())
            )
          );
          const allShifts = responses.flatMap(data => data.data || []);
          setShifts(allShifts);
        } else if (selectedSchedule) {
          // Single location mode: fetch shifts for selected schedule only
          const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts?scheduleId=${selectedSchedule.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await resp.json();
          if (resp.ok) {
            setShifts(data.data || []);
          }
        }
      } catch (e) {
        console.error('Shifts error:', e);
      }
    };
    fetchShifts();
  }, [selectedSchedule, selectedLocation, schedules]);

  // Fetch coverage summary for week (unmet totals per day) when schedule changes
  useEffect(() => {
    const fetchSummary = async () => {
      if (!selectedSchedule) return;
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ scheduleId: selectedSchedule.id });
        const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/coverage-summary?${params}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await resp.json();
        if (resp.ok) setCoverageSummary(data.data || []);
      } catch (e) { console.error(e); }
    };
    fetchSummary();
  }, [selectedSchedule]);

  // Fetch coverage totals
  useEffect(() => {
    const fetchCoverageTotals = async () => {
      if (!selectedSchedule) return;
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/coverage-totals?scheduleId=${selectedSchedule.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json();
        if (resp.ok) {
          setCoverageTotals(data.data || { daily: [], byEmployee: [] });
        }
      } catch (e) {
        console.error('Coverage totals error:', e);
      }
    };
    fetchCoverageTotals();
  }, [selectedSchedule, shifts]);

  // Filter employees and shifts
  const filteredEmployees = useMemo(() => {
    let filtered = employees;
    if (empQuery) {
      filtered = filtered.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(empQuery.toLowerCase()) ||
        emp.role?.toLowerCase().includes(empQuery.toLowerCase())
      );
    }
    if (empRole) {
      filtered = filtered.filter(emp => emp.role === empRole);
    }
    return filtered;
  }, [employees, empQuery, empRole]);

  const availableRoles = useMemo(() => {
    const roles = [...new Set(employees.map(e => e.role).filter(Boolean))];
    return roles.sort();
  }, [employees]);

  // API functions for shift operations
  const handleSaveShift = async (shiftData, shiftId) => {
    const token = localStorage.getItem('token');
    const url = shiftId
      ? `${API_URL.replace(/\/$/, '')}/vnext/shifts/${shiftId}`
      : `${API_URL.replace(/\/$/, '')}/vnext/shifts`;
    const method = shiftId ? 'PATCH' : 'POST';

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(shiftData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save shift');
    }

    // Refresh shifts after save
    if (selectedSchedule) {
      const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts?scheduleId=${selectedSchedule.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      if (resp.ok) {
        setShifts(data.data || []);
      }
    }
  };

  const handleDeleteShift = async (shiftId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts/${shiftId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete shift');
    }

    // Refresh shifts after delete
    if (selectedSchedule) {
      const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts?scheduleId=${selectedSchedule.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      if (resp.ok) {
        setShifts(data.data || []);
      }
    }
  };

  // Handle shift click to edit
  // replaced by action popover

  // Handle empty space click to create
  const handleEmptySpaceClick = (date, employee = null, defaults = {}, event = null) => {
    if (employee) {
      // quick create popover in roster cell
      setQuickCreate({ open: true, anchorEl: event?.currentTarget || null, date, employee, startTime: defaults.startTime || '09:00', endTime: defaults.endTime || '13:00' });
    } else {
      // calendar/month grid — pick employee flow
      setEmployeePickerDialog({ open: true, date, employee: null, startTime: defaults.startTime, endTime: defaults.endTime });
    }
  };

  // Handle employee selection from picker dialog
  const handleEmployeeSelect = ({ employee, date, startTime, endTime }) => {
    setShiftEditDialog({
      open: true,
      shift: null,
      selectedDate: date,
      preselectedEmployee: employee,
      defaultStartTime: startTime,
      defaultEndTime: endTime
    });
  };

  // Tooltip action handlers
  const handleShiftEdit = (shift) => {
    setShiftEditDialog({
      open: true,
      shift: shift,
      selectedDate: new Date(String(shift.start_ts).replace(' ', 'T')),
      preselectedEmployee: employees.find(e => e.id === shift.employee_id)
    });
  };

  const handleShiftCopy = (shift) => {
    // Create a copy of the shift for the next day
    const nextDay = new Date(String(shift.start_ts).replace(' ', 'T'));
    nextDay.setDate(nextDay.getDate() + 1);

    setShiftEditDialog({
      open: true,
      shift: null,
      selectedDate: nextDay,
      preselectedEmployee: employees.find(e => e.id === shift.employee_id),
      defaultStartTime: shift.start_ts.split(' ')[1] || '09:00',
      defaultEndTime: shift.end_ts.split(' ')[1] || '17:00',
      defaultRole: shift.role
    });
  };

  const handleShiftReassign = (shift) => {
    setEmployeePickerDialog({
      open: true,
      date: new Date(String(shift.start_ts).replace(' ', 'T')),
      shiftToReassign: shift
    });
  };

  const handleShiftContextMenu = (event, shift) => {
    setContextMenu({
      open: true,
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      shift
    });
  };

  const closeContextMenu = () => setContextMenu({ open: false, mouseX: null, mouseY: null, shift: null });

  const handleShiftDoubleClick = (event, shift) => {
    setInlineEditor({ open: true, anchorEl: event.currentTarget, shift });
  };
  const closeInlineEditor = () => setInlineEditor({ open: false, anchorEl: null, shift: null });

  const handleShiftClick = (event, shift) => {
    setInlineEditor({ open: true, anchorEl: event.currentTarget, shift });
  };
  const closeActionPopover = () => setActionPopover({ open: false, anchorEl: null, shift: null, employee: null });

  // Handle auto-schedule completion
  const handleAutoScheduleComplete = (result) => {
    setAutoScheduleDialog(false);

    // Refresh shifts after auto-scheduling
    const token = localStorage.getItem('token');

    if (result?.multiLocation) {
      // Multi-location mode: refresh all schedules
      const allScheduleIds = schedules.map(s => s.id);
      Promise.all(
        allScheduleIds.map(scheduleId =>
          fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts?scheduleId=${scheduleId}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => r.json())
        )
      ).then(responses => {
        const allShifts = responses.flatMap(data => data.data || []);
        setShifts(allShifts);
      }).catch(console.error);
    } else if (selectedSchedule) {
      // Single location mode: refresh just the current schedule
      fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts?scheduleId=${selectedSchedule.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => response.json())
      .then(data => {
        if (data.data) {
          setShifts(data.data);
        }
      })
      .catch(console.error);
    }
  };

  // Assign a specific candidate from Coverage dialog to fill unmet
  const handleCoverageAssignCandidate = async (reqRow, candidate) => {
    try {
      if (!selectedSchedule) return;
      const token = localStorage.getItem('token');
      const date = coverageDialog.date; // YYYY-MM-DD
      const start = reqRow.start_time?.length === 5 ? `${reqRow.start_time}:00` : (reqRow.start_time || '09:00:00');
      const end = reqRow.end_time?.length === 5 ? `${reqRow.end_time}:00` : (reqRow.end_time || '17:00:00');
      const employeeId = candidate?.id || candidate?.employee_id || null;
      if (!employeeId) { alert('Missing employee id for candidate'); return; }

      const payload = {
        schedule_id: selectedSchedule.id,
        role: reqRow.role,
        start_ts: `${date} ${start}`,
        end_ts: `${date} ${end}`,
        break_minutes: 0,
        employee_id: employeeId,
        notes: null
      };

      // Save the shift
      await handleSaveShift(payload);

      // Refresh coverage summary for up-to-date unmet numbers
      try {
        const params = new URLSearchParams({ scheduleId: selectedSchedule.id, date });
                              params.set('overtimeThresholdHours', String(otHours));
                              const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/coverage?${params}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await resp.json();
        if (resp.ok) setCoverageDialog({ open: true, date: data.date, results: data.data });
      } catch (e) { /* silent */ }

      try {
        const sum = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/coverage-summary?scheduleId=${selectedSchedule.id}`, { headers: { Authorization: `Bearer ${token}` } });
        const sumd = await sum.json(); if (sum.ok) setCoverageSummary(sumd.data || []);
      } catch (e) { /* silent */ }
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to assign candidate');
    }
  };

  const handleCreateOpenShiftForRequirement = async (reqRow) => {
    try {
      if (!selectedSchedule) return;
      const token = localStorage.getItem('token');
      const date = coverageDialog.date; // YYYY-MM-DD
      const start = reqRow.start_time?.length === 5 ? `${reqRow.start_time}:00` : (reqRow.start_time || '09:00:00');
      const end = reqRow.end_time?.length === 5 ? `${reqRow.end_time}:00` : (reqRow.end_time || '17:00:00');
      let count = Number(window.prompt('How many open shifts to create?', String(Math.max(1, Number(reqRow.unmet||1)))))
      if (!Number.isFinite(count) || count <= 0) count = 1;
      for (let i=0;i<count;i++) {
        const payload = {
          schedule_id: selectedSchedule.id,
          role: reqRow.role,
          start_ts: `${date} ${start}`,
          end_ts: `${date} ${end}`,
          break_minutes: 0,
          employee_id: null,
          notes: 'Open shift from Coverage Check'
        };
        // eslint-disable-next-line no-await-in-loop
        await handleSaveShift(payload);
      }
      // Refresh coverage data
        const params = new URLSearchParams({ scheduleId: selectedSchedule.id, date, overtimeThresholdHours: String(otHours) });
        const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/coverage?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await resp.json();
      if (resp.ok) setCoverageDialog({ open: true, date: data.date, results: data.data });
      const sum = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/coverage-summary?scheduleId=${selectedSchedule.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const sumd = await sum.json(); if (sum.ok) setCoverageSummary(sumd.data || []);
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to create open shift');
    }
  };

  // Filters for coverage dialog candidates
  const [coverageFilters, setCoverageFilters] = React.useState({ includePartial: false, includeConflicts: false, includeMismatches: false });
  const setFilter = (k) => (e) => setCoverageFilters(prev => ({ ...prev, [k]: e.target.checked }));
  // Row-level filter: only show unmet requirements (persist per user)
  const [coverageRowFilters, setCoverageRowFilters] = React.useState({ onlyUnmet: true });
  const setRowFilter = (k) => (e) => setCoverageRowFilters(prev => ({ ...prev, [k]: e.target.checked }));
  // Overtime threshold (hours), configurable and persisted per user
  const [otHours, setOtHours] = React.useState(40);
  React.useEffect(() => {
    try {
      const key = `dhr_ot_hours_${(user && (user.id || user.email)) || 'anon'}`;
      const v = localStorage.getItem(key);
      if (v !== null) setOtHours(Math.max(1, Number(v)) || 40);
    } catch {}
  }, [user?.id, user?.email]);
  React.useEffect(() => {
    try {
      const key = `dhr_ot_hours_${(user && (user.id || user.email)) || 'anon'}`;
      localStorage.setItem(key, String(otHours));
    } catch {}
  }, [otHours, user?.id, user?.email]);
  React.useEffect(() => {
    try {
      const key = `dhr_cov_only_unmet_${(user && (user.id || user.email)) || 'anon'}`;
      const v = localStorage.getItem(key);
      if (v !== null) setCoverageRowFilters(prev => ({ ...prev, onlyUnmet: v === 'true' }));
    } catch {}
  }, [user?.id, user?.email]);
  React.useEffect(() => {
    try {
      const key = `dhr_cov_only_unmet_${(user && (user.id || user.email)) || 'anon'}`;
      localStorage.setItem(key, String(coverageRowFilters.onlyUnmet));
    } catch {}
  }, [coverageRowFilters.onlyUnmet, user?.id, user?.email]);

  // Review dialog state (per-requirement detailed candidates)
  const [coverageReview, setCoverageReview] = React.useState({ open: false, req: null });
  const openCoverageReview = (req) => setCoverageReview({ open: true, req });
  const closeCoverageReview = () => setCoverageReview({ open: false, req: null });

  // Helpers
  const to12h = (t) => {
    if (!t) return '';
    const [H, M] = String(t).split(':').map(Number);
    let h = H % 12; if (h === 0) h = 12;
    const ampm = H < 12 ? 'AM' : 'PM';
    return `${h}:${String(M||0).padStart(2,'0')} ${ampm}`;
  };

  const timeToMinutes = (t) => { if (!t) return 0; const [h,m] = String(t).split(':').map(Number); return (h||0)*60 + (m||0); };
  const pct = (m) => `${(m/ (24*60) * 100).toFixed(4)}%`;
  const buildTimeline = (reqRow, candidate) => {
    const rS0 = timeToMinutes(reqRow.start_time); let rE0 = timeToMinutes(reqRow.end_time); if (rE0 <= rS0) rE0 += 24*60;
    // Box container with 24h width, requirement as outline, availability as green, conflicts as amber/red
    const segments = [];
    // Availability segments
    const av = Array.isArray(candidate.avail) ? candidate.avail : [];
    av.forEach(({ start, end }) => {
      let aS = timeToMinutes(start); let aE = timeToMinutes(end); if (aE <= aS) aE += 24*60;
      segments.push({ kind: 'avail', left: pct(aS), width: pct(Math.max(0, aE - aS)) });
    });
    // Conflict segments (from shifts)
    const cfs = Array.isArray(candidate.conflicts) ? candidate.conflicts : [];
    cfs.forEach(({ start, end }) => {
      let cS = timeToMinutes(start); let cE = timeToMinutes(end); if (cE <= cS) cE += 24*60;
      segments.push({ kind: 'conflict', left: pct(cS), width: pct(Math.max(0, cE - cS)) });
    });
    // Requirement segment
    const reqSeg = { kind: 'req', left: pct(rS0), width: pct(Math.max(0, rE0 - rS0)) };
    return { segments, reqSeg };
  };

  // Show loading for initial data fetch, but not for subsequent updates
  if (loading && schedules.length === 0 && employees.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{
      m: 3,
      height: 'calc(100vh - 140px)',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      {error && <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>}

      {/* Header */}
      <Box sx={{
        p: 3,
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px 8px 0 0'
      }}>
        <Stack direction="row" spacing={3.5} alignItems="center">
          {/* Navigation */}
          <Stack direction="row" spacing={1.25} alignItems="center">
            <IconButton onClick={() => navigate(-1)} size="small" sx={{ p: 0.5 }}>
              <ChevronLeft />
            </IconButton>
            <Button variant="outlined" onClick={goToToday} startIcon={<Today />} size="small" sx={{ px: 0.75, py: 0.25, minWidth: 0 }}>
              Today
            </Button>
            <IconButton onClick={() => navigate(1)} size="small" sx={{ p: 0.5 }}>
              <ChevronRight />
            </IconButton>
          </Stack>

          <Typography variant="h5" sx={{
            minWidth: 250,
            fontWeight: 600,
            color: '#1a1a1a'
          }}>
            {getHeaderTitle()}
          </Typography>

          {/* View Mode Selector (compact toggles) */}
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(e, mode) => { if (mode) setViewMode(mode); }}
            sx={{
              columnGap: 1,
              '& .MuiToggleButton-root': {
                px: 0.75,
                py: 0.25,
                minWidth: 0,
                fontSize: 12,
                lineHeight: 1,
                borderRadius: 1.5
              }
            }}
          >
            <ToggleButton value="day" aria-label="Day view">
              <ViewDay sx={{ fontSize: 16, mr: 0.5 }} />
              Day
            </ToggleButton>
            <ToggleButton value="week" aria-label="Week view">
              <ViewWeek sx={{ fontSize: 16, mr: 0.5 }} />
              Week
            </ToggleButton>
            <ToggleButton value="2week" aria-label="2 Week view">
              <ViewModule sx={{ fontSize: 16, mr: 0.5 }} />
              2W
            </ToggleButton>
            <ToggleButton value="month" aria-label="Month view">
              <CalendarMonth sx={{ fontSize: 16, mr: 0.5 }} />
              Month
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Employee Filters */}
          <Stack direction="row" spacing={1.25}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Location</InputLabel>
              <Select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)} label="Location">
                <MenuItem value="all">All Locations</MenuItem>
                {locations.map(loc => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search employees..."
              value={empQuery}
              onChange={(e) => setEmpQuery(e.target.value)}
              sx={{ minWidth: 150 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select value={empRole} onChange={(e) => setEmpRole(e.target.value)} label="Role">
                <MenuItem value="">All Roles</MenuItem>
                {availableRoles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          {selectedSchedule && (
            <Button
              variant="outlined"
              startIcon={<AutoMode />}
              size="small"
              onClick={() => setAutoScheduleDialog(true)}
              sx={{ mr: 1, px: 1, py: 0.25, fontSize: 12, minWidth: 0 }}
            >
              Auto-Schedule
            </Button>
          )}

          {selectedSchedule && SCHED_FEATURES && (
            <Button
              variant="outlined"
              size="small"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/generate-shifts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ schedule_id: selectedSchedule.id })
                  });
                  const data = await resp.json();
                  if (!resp.ok) throw new Error(data.error || 'Failed to generate');
                  // reload shifts
                  const s = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts?scheduleId=${selectedSchedule.id}`, { headers: { Authorization: `Bearer ${token}` } });
                  const sd = await s.json();
                  if (s.ok) setShifts(sd.data || []);
                } catch (e) {
                  console.error(e);
                  alert(e.message);
                }
              }}
              sx={{ mr: 1, px: 1, py: 0.25, fontSize: 12, minWidth: 0 }}
            >
              Generate From Requirements
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<Add />}
            size="small"
            onClick={() => handleEmptySpaceClick(new Date())}
            sx={{ px: 1, py: 0.25, fontSize: 12, minWidth: 0 }}
          >
            Add Shift
          </Button>
        </Stack>
      </Box>

      {/* Calendar Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {!selectedSchedule ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No schedules found for this period. Create a schedule to start adding shifts.
          </Alert>
        ) : (
          <>
            {/* Day Headers (disabled; rendered sticky inside scroll area) */}
            <Box sx={{ display: 'none' }} />

            {/* Calendar Body */}
            <DndContext onDragEnd={onDragEnd}>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {/* Sticky Day Headers inside scroll area for alignment */}
              <Box sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: viewMode === 'month' ? 'repeat(7, 1fr)' : `220px repeat(${dateRange.length}, minmax(140px, 1fr))` }}>
                  {viewMode !== 'month' && (
                    <Box sx={{ p: 1.25, borderRight: '1px solid', borderColor: 'divider', fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
                      Employee
                    </Box>
                  )}
                  {(viewMode === 'month' ? dateRange.slice(0, 7) : dateRange).map((day, index) => {
                    const isToday = day.toDateString() === new Date().toDateString();
                    const dayName = day.toLocaleDateString('en-US', { weekday: 'short' });
                    const dayNumber = day.getDate();
                    const totalDayHours = shifts
                      .filter(shift => new Date(String(shift.start_ts).replace(' ', 'T')).toDateString() === day.toDateString())
                      .reduce((sum, s) => {
                        const s1 = new Date(String(s.start_ts).replace(' ', 'T'));
                        const e1 = new Date(String(s.end_ts).replace(' ', 'T'));
                        return sum + (e1 - s1) / (1000 * 60 * 60);
                      }, 0);
                    return (
                      <Box key={index} sx={{ px: 1, py: 0.5, textAlign: 'center', borderRight: index < (viewMode === 'month' ? 6 : dateRange.length - 1) ? '1px solid' : 'none', borderColor: 'divider', minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: isToday ? 'primary.dark' : 'text.primary' }}>
                          {dayName} {dayNumber}
                        </Typography>
                        {viewMode !== 'month' && (
                          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
                            {totalDayHours > 0 ? `${totalDayHours.toFixed(1)}h` : '0.0h'}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              {viewMode === 'month' ? (
                // Month view - calendar grid
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gridTemplateRows: 'repeat(6, 1fr)',
                  height: '100%'
                }}>
                  {dateRange.map((day, dayIndex) => {
                    const dayShifts = shifts.filter(shift => {
                      const shiftStart = parseLocal(shift.start_ts);
                      return shiftStart.toDateString() === day.toDateString();
                    });
                    const isCurrentMonth = day.getMonth() === selectedDate.getMonth();
                    const isToday = day.toDateString() === new Date().toDateString();

                    return (
                      <Box
                        key={dayIndex}
                        sx={{
                          border: '1px solid #e0e0e0',
                          p: 0.5,
                          backgroundColor: !isCurrentMonth ? '#fafafa' : (isToday ? '#e3f2fd' : '#fff'),
                          opacity: !isCurrentMonth ? 0.6 : 1,
                          minHeight: 100,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          position: 'relative',
                          '&:hover': {
                            backgroundColor: !isCurrentMonth ? '#fafafa' : (isToday ? '#d1ecf8' : '#f5f5f5')
                          }
                        }}
                        onClick={(e) => {
                          // Only trigger empty space click if no shift was clicked
                          if (e.target === e.currentTarget || e.target.closest('.shift-block') === null) {
                            const bounds = e.currentTarget.getBoundingClientRect();
                            const relY = (e.clientY - bounds.top) / Math.max(1, bounds.height);
                            const defaultStart = relY < 0.5 ? '09:00' : '13:00';
                            const defaultEnd = relY < 0.5 ? '13:00' : '17:00';
                            handleEmptySpaceClick(day, null, { startTime: defaultStart, endTime: defaultEnd }, e);
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {day.getDate()}
                        </Typography>
                        {dayShifts.slice(0, 3).map(shift => {
                          const employee = employees.find(e => e.id === shift.employee_id);
                          return (
                            <Box key={shift.id} className="shift-block">
                              <ShiftBlock
                                shift={shift}
                                employee={employee}
                                onClick={(e) => handleShiftClick(e, shift)}
                                isCompact={true}
                                onEdit={handleShiftEdit}
                                onDelete={handleDeleteShift}
                                onCopy={handleShiftCopy}
                                onReassign={handleShiftReassign}
                                onContextMenu={handleShiftContextMenu}
                                onDoubleClick={handleShiftDoubleClick}
                              />
                            </Box>
                          );
                        })}
                        {dayShifts.length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            +{dayShifts.length - 3} more
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                // Week/Day view - employee roster
                <Box sx={{ display: 'grid', gridTemplateColumns: `220px repeat(${dateRange.length}, minmax(140px, 1fr))`, minHeight: 'fit-content', backgroundColor: 'background.paper' }}>
                  {filteredEmployees.length > 0 ? (
                    filteredEmployees.map(employee => {
                      const employeeShifts = shifts.filter(shift => shift.employee_id === employee.id);
                      return (
                        <EmployeeRow
                          key={employee.id}
                          employee={employee}
                          shifts={employeeShifts}
                          days={dateRange}
                          onShiftClick={handleShiftClick}
                          onEmptySpaceClick={handleEmptySpaceClick}
                          showHours={viewMode === 'week' || viewMode === '2week'}
                          onShiftEdit={handleShiftEdit}
                          onShiftDelete={handleDeleteShift}
                          onShiftCopy={handleShiftCopy}
                          onShiftReassign={handleShiftReassign}
                          onShiftContextMenu={handleShiftContextMenu}
                          onShiftDoubleClick={handleShiftDoubleClick}
                        />
                      );
                    })
                  ) : (
                    <Box sx={{
                      gridColumn: `1 / -1`,
                      p: 4,
                      textAlign: 'center',
                      color: 'text.secondary'
                    }}>
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Typography>No employees found for this location</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            </DndContext>

            {/* Unmet Summary Row (under calendar) */}
            {viewMode !== 'month' && selectedSchedule && (
              <Box sx={{ display: 'grid', gridTemplateColumns: `220px repeat(${dateRange.length}, minmax(140px, 1fr))`, borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
                <Box sx={{ p: 0.75, borderRight: '1px solid #e5e7eb', color: 'text.secondary', fontSize: 12, fontWeight: 600 }}>
                  Unmet
                </Box>
                {dateRange.map((day, index) => {
                  const sum = coverageSummary.find(s => s.date === day.toISOString().slice(0,10));
                  const unmet = sum?.unmet || 0;
                  return (
                    <Box key={index} sx={{ p: 0.5, textAlign: 'center', borderRight: index < dateRange.length - 1 ? '1px solid #e5e7eb' : 'none', minHeight: 28 }}>
                      {unmet > 0 ? (
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ flexWrap: 'wrap' }}>
                          {/* Role-level chips if available */}
                          {Array.isArray(sum?.roles) && sum.roles.length > 0 ? (
                            sum.roles.filter(r => r.unmet > 0).slice(0, 4).map((r, idx) => (
                              <Chip key={idx} size="small" label={`${r.role}: ${r.unmet}`} color="error" sx={{ height: 20, fontSize: 11 }} />
                            ))
                          ) : (
                            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}> {unmet} </Typography>
                          )}
                          {SCHED_FEATURES && (
                            <>
                        <Button size="small" variant="text" sx={{ px: 0.5, py: 0.1, minWidth: 0, fontSize: 10 }} onClick={async ()=>{
                          try {
                            const token = localStorage.getItem('token');
                            const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/fill-unmet`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ schedule_id: selectedSchedule.id, date: formatLocalDate(day) })
                            });
                                  const data = await resp.json();
                                  if (!resp.ok) throw new Error(data.error || 'Fill unmet failed');
                                  // refresh shifts and coverage summary
                                  const s = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/shifts?scheduleId=${selectedSchedule.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                  const sd = await s.json(); if (s.ok) setShifts(sd.data || []);
                                  const sumr = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/coverage-summary?scheduleId=${selectedSchedule.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                  const sumd = await sumr.json(); if (sumr.ok) setCoverageSummary(sumd.data || []);
                                } catch (e) { console.error(e); alert(e.message); }
                              }}>Fill</Button>
                              <IconButton size="small" sx={{ p: 0.4 }} onClick={async ()=>{
        try {
          const token = localStorage.getItem('token');
          const params = new URLSearchParams({ scheduleId: selectedSchedule.id, date: formatLocalDate(day), overtimeThresholdHours: String(otHours) });
          const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/coverage?${params}`, { headers: { Authorization: `Bearer ${token}` } });
                                  const data = await resp.json();
                                  if (!resp.ok) throw new Error(data.error || 'Coverage check failed');
                                  setCoverageDialog({ open: true, date: data.date, results: data.data });
                                } catch (e) { console.error(e); }
                              }}>
                                <FactCheckIcon fontSize="inherit" />
                              </IconButton>
                            </>
                          )}
                        </Stack>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Hours Summary */}
      {selectedSchedule && filteredEmployees.length > 0 && (
        <HoursSummary
          employees={filteredEmployees}
          shifts={shifts}
          dateRange={dateRange}
          viewMode={viewMode}
        />
      )}

      {/* Shift Edit Dialog */}
      <ShiftEditDialog
        open={shiftEditDialog.open}
        onClose={() => setShiftEditDialog({ open: false, shift: null, selectedDate: null })}
        shift={shiftEditDialog.shift}
        employees={employees}
        selectedDate={shiftEditDialog.selectedDate}
        defaultStartTime={shiftEditDialog.defaultStartTime}
        defaultEndTime={shiftEditDialog.defaultEndTime}
        preselectedEmployee={shiftEditDialog.preselectedEmployee}
        defaultRole={shiftEditDialog.defaultRole}
        onSave={handleSaveShift}
        onDelete={handleDeleteShift}
        scheduleId={selectedSchedule?.id}
      />

      {/* Auto-Schedule Dialog */}
      <AutoScheduleDialog
        open={autoScheduleDialog}
        onClose={() => setAutoScheduleDialog(false)}
        scheduleId={selectedSchedule?.id}
        schedules={schedules}
        locations={locations}
        onComplete={handleAutoScheduleComplete}
      />

      {/* Employee Picker Dialog */}
      <EmployeePickerDialog
        open={employeePickerDialog.open}
        onClose={() => setEmployeePickerDialog({ open: false, date: null, employee: null })}
        onEmployeeSelect={handleEmployeeSelect}
        selectedDate={employeePickerDialog.date}
        startTime={employeePickerDialog.startTime || '09:00'}
        endTime={employeePickerDialog.endTime || '17:00'}
        locationId={user?.store}
      />

      {/* Auto-schedule scoped to a single day */}


      {/* Coverage vs Requirements dialog */}
      {coverageDialog.open && (
        <Dialog open onClose={()=>setCoverageDialog({ open:false, date:null, results: [] })} maxWidth="md" fullWidth>
          <DialogTitle>Coverage Check — {coverageDialog.date}</DialogTitle>
          <DialogContent>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <FormControlLabel control={<Checkbox size="small" checked={coverageRowFilters.onlyUnmet} onChange={setRowFilter('onlyUnmet')} />} label={<Typography variant="caption">Only show unmet</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={coverageFilters.includePartial} onChange={setFilter('includePartial')} />} label={<Typography variant="caption">Include partial</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={coverageFilters.includeConflicts} onChange={setFilter('includeConflicts')} />} label={<Typography variant="caption">Include conflicts</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={coverageFilters.includeMismatches} onChange={setFilter('includeMismatches')} />} label={<Typography variant="caption">Include role mismatch</Typography>} />
              <TextField size="small" label="OT threshold (h)" type="number" value={otHours} onChange={(e)=>setOtHours(Math.max(1, Number(e.target.value||40)))} sx={{ width: 130 }} />
              <Button size="small" variant="outlined" onClick={async ()=>{
                try {
                  if (!selectedSchedule || !coverageDialog.date) return;
                  const token = localStorage.getItem('token');
                  const params = new URLSearchParams({ scheduleId: selectedSchedule.id, date: coverageDialog.date, overtimeThresholdHours: String(otHours) });
                  const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/coverage?${params}`, { headers: { Authorization: `Bearer ${token}` } });
                  const data = await resp.json();
                  if (resp.ok) setCoverageDialog({ open: true, date: data.date, results: data.data });
                } catch (e) { console.error(e); }
              }}>Refresh</Button>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip size="small" label="match/full" color="success" />
              <Chip size="small" label="partial" variant="outlined" />
              <Chip size="small" label="mismatch" color="info" variant="outlined" />
              <Chip size="small" label="conflict" color="warning" />
              <Chip size="small" label="overtime" color="error" />
            </Stack>
            {coverageDialog.results.length === 0 ? (
              <Alert severity="info">No staffing requirements defined for this date.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Role</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell align="right">Required</TableCell>
                    <TableCell align="right">Assigned</TableCell>
                    <TableCell align="right">Open</TableCell>
                    <TableCell align="right">Unmet</TableCell>
                    <TableCell>Candidates</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coverageDialog.results
                    .filter(r => !coverageRowFilters.onlyUnmet || (r.unmet || 0) > 0)
                    .map((r,i)=> (
                    <TableRow key={i}>
                      <TableCell>{r.role}</TableCell>
                      <TableCell>{r.start_time}–{r.end_time}</TableCell>
                      <TableCell align="right">{r.required}</TableCell>
                      <TableCell align="right">{r.assigned}</TableCell>
                      <TableCell align="right">{r.open}</TableCell>
                      <TableCell align="right" style={{ color: r.unmet>0 ? '#d32f2f' : undefined }}>{r.unmet}</TableCell>
                      <TableCell>
                        {Array.isArray(r.candidates) && r.candidates.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                            {r.candidates
                              .filter(c => coverageFilters.includePartial || !c.partial)
                              .filter(c => coverageFilters.includeConflicts || !c.conflict)
                              .filter(c => coverageFilters.includeMismatches || !c.roleMismatch)
                              .slice(0,3).map((c, idx) => {
                                const flags = [
                                  c.overtime ? 'overtime' : null,
                                  c.conflict ? 'conflict' : null,
                                  c.partial ? 'partial' : null,
                                  c.roleMismatch ? 'mismatch' : null
                                ].filter(Boolean);
                                const label = flags.length ? `${c.name} (${flags.join(', ')})` : c.name;
                                const chipColor = c.overtime ? 'error' : (c.conflict ? 'warning' : (c.roleMismatch ? 'info' : (c.partial ? 'default' : 'success')));
                                const reqRange = `${r.start_time}–${r.end_time}`;
                                const overlapMin = Number(c.overlapMinutes || 0);
                                const reqMin = Number(c.requiredMinutes || 0);
                                const overlapHours = (overlapMin / 60).toFixed(1).replace(/\.0$/, '');
                                const reqHours = (reqMin / 60).toFixed(1).replace(/\.0$/, '');
                                const availText = Array.isArray(c.avail) && c.avail.length ? c.avail.slice(0,3).map(a => `${a.start}–${a.end}`).join(', ') : 'No availability on file';
                                const conflictsText = Array.isArray(c.conflicts) && c.conflicts.length ? c.conflicts.map(cf => `${cf.role} ${cf.start}–${cf.end}`).join('; ') : 'None';
                                const otMin = Number(c.overtimeMinutes || 0);
                                const otHours = (c.overtimeMinutes ? (Number(c.overtimeMinutes)/60) : 0).toFixed(1).replace(/\.0$/,'');
                                const curH = ((c.currentWeekMinutes||0)/60).toFixed(1).replace(/\.0$/,'');
                                const projH = ((c.projectedWeekMinutes||0)/60).toFixed(1).replace(/\.0$/,'');
                                const explain = [
                                  c.roleMismatch ? `Role mismatch: ${c.role} vs ${r.role}` : null,
                                  (c.partial && overlapMin > 0 && reqMin > 0) ? `Partial availability: covers ${overlapHours}/${reqHours}h for ${reqRange}` : null,
                                  `Availability: ${availText}`,
                                  c.conflict ? `Conflicts: ${conflictsText}` : null,
                                  otMin > 0 ? `Overtime: +${otHours}h (week ${curH}→${projH}h)` : null
                                ].filter(Boolean).join("\n");
                                return (
                                  <Tooltip key={idx} title={<span style={{ whiteSpace: 'pre-line' }}>{explain}</span>} arrow>
                                    <Chip
                                      size="small"
                                      label={label}
                                      color={chipColor}
                                      variant={c.partial || c.roleMismatch ? 'outlined' : 'filled'}
                                      sx={{ mb: 0.5, cursor: 'pointer' }}
                                      onClick={() => handleCoverageAssignCandidate(r, c)}
                                    />
                                  </Tooltip>
                                );
                              })}
                            <Button size="small" variant="text" sx={{ ml: 0.5 }} onClick={()=>openCoverageReview(r)}>
                              Review ({r.candidates.length})
                            </Button>
                            <Button size="small" variant="outlined" sx={{ ml: 0.5 }} onClick={()=>handleCreateOpenShiftForRequirement(r)}>
                              Create Open Shift
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">No candidates</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>setCoverageDialog({ open:false, date:null, results: [] })}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Candidate Review Dialog */}
      {coverageReview.open && coverageReview.req && (
        <Dialog open onClose={closeCoverageReview} maxWidth="md" fullWidth>
          <DialogTitle>Review Candidates — {coverageReview.req.role} ({to12h(coverageReview.req.start_time)}–{to12h(coverageReview.req.end_time)})</DialogTitle>
          <DialogContent>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <FormControlLabel control={<Checkbox size="small" checked={coverageFilters.includePartial} onChange={setFilter('includePartial')} />} label={<Typography variant="caption">Include partial</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={coverageFilters.includeConflicts} onChange={setFilter('includeConflicts')} />} label={<Typography variant="caption">Include conflicts</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={coverageFilters.includeMismatches} onChange={setFilter('includeMismatches')} />} label={<Typography variant="caption">Include mismatch</Typography>} />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Timeline</TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell align="right">Coverage</TableCell>
                  <TableCell align="right">Overtime</TableCell>
                  <TableCell>Conflicts</TableCell>
                  <TableCell>Reasons</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coverageReview.req.candidates
                  .filter(c => coverageFilters.includePartial || !c.partial)
                  .filter(c => coverageFilters.includeConflicts || !c.conflict)
                  .filter(c => coverageFilters.includeMismatches || !c.roleMismatch)
                  .sort((a,b)=> (b.score||0) - (a.score||0))
                  .map((c, idx) => {
                    const coverage = `${((c.overlapMinutes||0)/60).toFixed(1).replace(/\.0$/,'')}/${((c.requiredMinutes||0)/60).toFixed(1).replace(/\.0$/,'')}h`;
                    const conflictsText = Array.isArray(c.conflicts) && c.conflicts.length ? c.conflicts.map(cf => `${cf.role} ${cf.start}–${cf.end}`).join('; ') : 'None';
                    const matchFlags = [c.roleMismatch ? 'mismatch' : 'match', c.partial ? 'partial' : 'full', c.conflict ? 'conflict' : 'clear'].join(', ');
                    const tl = buildTimeline(coverageReview.req, c);
                    return (
                      <TableRow key={idx}>
                        <TableCell>{c.name}</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Box sx={{ position: 'relative', height: 10, backgroundColor: '#f3f4f6', borderRadius: 1, overflow: 'hidden' }}>
                            {/* Availability */}
                            {tl.segments.filter(s=>s.kind==='avail').map((s,i)=> (
                              <Box key={`a${i}`} sx={{ position: 'absolute', left: s.left, width: s.width, top: 0, bottom: 0, backgroundColor: '#a7f3d0' }} />
                            ))}
                            {/* Conflicts */}
                            {tl.segments.filter(s=>s.kind==='conflict').map((s,i)=> (
                              <Box key={`c${i}`} sx={{ position: 'absolute', left: s.left, width: s.width, top: 0, bottom: 0, backgroundColor: '#fde68a' }} />
                            ))}
                            {/* Requirement outline */}
                            <Box sx={{ position: 'absolute', left: tl.reqSeg.left, width: tl.reqSeg.width, top: 0, bottom: 0, border: '2px solid #60a5fa', borderRadius: 1, pointerEvents: 'none' }} />
                          </Box>
                          <Typography variant="caption" color="text.secondary">Req {to12h(coverageReview.req.start_time)}–{to12h(coverageReview.req.end_time)}</Typography>
                        </TableCell>
                        <TableCell>{matchFlags}</TableCell>
                        <TableCell align="right">{coverage}</TableCell>
                        <TableCell align="right">
                          {c.overtime ? (
                            <Typography variant="caption" color="error.main">+{((c.overtimeMinutes||0)/60).toFixed(1).replace(/\.0$/,'')}h</Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">None</Typography>
                          )}
                        </TableCell>
                        <TableCell>{conflictsText}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {(Array.isArray(c.reasons) ? c.reasons : []).slice(0,4).join(' • ')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="contained" onClick={()=>{ handleCoverageAssignCandidate(coverageReview.req, c); closeCoverageReview(); }}>Assign</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCoverageReview}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Inline Editor */}
      <InlineShiftEditor
        open={inlineEditor.open}
        anchorEl={inlineEditor.anchorEl}
        shift={inlineEditor.shift}
        onClose={closeInlineEditor}
        onSave={handleSaveShift}
        onDelete={handleDeleteShift}
        onOpen={()=>{ if (inlineEditor.shift){ closeInlineEditor(); handleShiftEdit(inlineEditor.shift); } }}
      />

      {/* Shift Action Popover */}
      <ShiftActionPopover
        open={actionPopover.open}
        anchorEl={actionPopover.anchorEl}
        onClose={closeActionPopover}
        shift={actionPopover.shift}
        employee={actionPopover.employee}
        roles={availableRoles}
        onOpenShift={() => actionPopover.shift && handleShiftEdit(actionPopover.shift)}
        onPickEmployee={(role)=>{
          const sh = actionPopover.shift;
          if (!sh) return;
          closeActionPopover();
          // open employee picker; when chosen, save with selected role
          setEmployeePickerDialog({ open: true, date: new Date(String(sh.start_ts).replace(' ', 'T')), shiftToReassign: { ...sh, role } });
        }}
      />

      {/* Quick Create Popover */}
      <QuickCreatePopover
        open={quickCreate.open}
        anchorEl={quickCreate.anchorEl}
        onClose={()=>setQuickCreate({ open:false, anchorEl:null, date:null, employee:null, startTime:'09:00', endTime:'13:00' })}
        date={quickCreate.date}
        employee={quickCreate.employee}
        defaultStartTime={quickCreate.startTime}
        defaultEndTime={quickCreate.endTime}
        roles={availableRoles}
        onCreateAssigned={(role)=>{
          if (!quickCreate.date || !quickCreate.employee) return;
          const dateStr = `${quickCreate.date.getFullYear()}-${String(quickCreate.date.getMonth()+1).padStart(2,'0')}-${String(quickCreate.date.getDate()).padStart(2,'0')}`;
          const start_ts = `${dateStr} ${quickCreate.startTime}:00`;
          const end_ts = `${dateStr} ${quickCreate.endTime}:00`;
          handleSaveShift({
            schedule_id: selectedSchedule?.id,
            role,
            start_ts,
            end_ts,
            break_minutes: 0,
            employee_id: quickCreate.employee.id,
            notes: null
          });
        }}
        onCreateOpen={(role)=>{
          if (!quickCreate.date) return;
          const dateStr = `${quickCreate.date.getFullYear()}-${String(quickCreate.date.getMonth()+1).padStart(2,'0')}-${String(quickCreate.date.getDate()).padStart(2,'0')}`;
          const start_ts = `${dateStr} ${quickCreate.startTime}:00`;
          const end_ts = `${dateStr} ${quickCreate.endTime}:00`;
          handleSaveShift({
            schedule_id: selectedSchedule?.id,
            role,
            start_ts,
            end_ts,
            break_minutes: 0,
            employee_id: null,
            notes: null
          });
        }}
        onMoreOptions={()=>{
          if (!quickCreate.date) return;
          setShiftEditDialog({
            open: true,
            shift: null,
            selectedDate: quickCreate.date,
            preselectedEmployee: quickCreate.employee,
            defaultStartTime: quickCreate.startTime,
            defaultEndTime: quickCreate.endTime
          });
        }}
      />

      {/* Shift Context Menu */}
      <Menu
        open={contextMenu.open}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu.open ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <MenuItem onClick={() => { closeContextMenu(); if (contextMenu.shift) handleShiftEdit(contextMenu.shift); }}>Edit</MenuItem>
        <MenuItem onClick={() => { closeContextMenu(); if (contextMenu.shift) handleShiftCopy(contextMenu.shift); }}>Copy to Next Day</MenuItem>
        <MenuItem onClick={() => { closeContextMenu(); if (contextMenu.shift) handleShiftReassign(contextMenu.shift); }}>Reassign</MenuItem>
        <MenuItem onClick={() => { const s=contextMenu.shift; closeContextMenu(); if (s?.id) handleDeleteShift(s.id); }} style={{ color: '#dc2626' }}>Delete</MenuItem>
      </Menu>
    </Paper>
  );
}
